import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { catchError, debounceTime, of, Subscription, filter, switchMap, take } from 'rxjs';
import { AjaxService } from '../../../services/ajax.service';
import { DirectionService } from '../../../services/direction.service';
import { TranslateService } from '../../../services/translate.service';
import { DISP_JR_FOR_USER } from '../../../services/account-api.constants';
import { ViewScopeService, Customer } from '../../../services/view-scope.service';
import { LayoutService } from '../../../layout/service/layout.service';

interface JournalTypeRow {
    MT: number;
    Descr: string;
    JournalRows: number;
}

interface JournalTypeSegment {
    mt: number;
    label: string;
    rows: number;
    percent: number;
}

@Component({
    standalone: true,
    selector: 'app-journal-movedist-widget',
    imports: [CommonModule, ChartModule],
    templateUrl: './jornalmovedistwidget.html',
    styleUrl: './jornalmovedistwidget.scss'
})
export class JournalMoveDistWidget implements OnInit, OnDestroy {
    segments: JournalTypeSegment[] = [];
    chartData: any;
    chartOptions: any;
    chartPlugins: any[] = [];
    totalRows = 0;
    loading = false;
    error: string | null = null;
    subscription: Subscription;

    isRtl = true;

    constructor(
        private ajaxService: AjaxService,
        public viewScopeService: ViewScopeService,
        private layoutService: LayoutService,
        private directionService: DirectionService,
        public translateServ: TranslateService
    ) {
        this.isRtl = this.directionService.isRtl();
        this.subscription = this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.isRtl = this.directionService.isRtl();
            this.setChart();
        });
    }

    ngOnInit(): void {
        this.loadJournalRows();
    }

    // Waits for ViewScopeService's selectedCustomer$ (BehaviorSubject, always replays its latest
    // value) instead of reading it synchronously - avoids racing SelectAccountComponent, which
    // populates the selected customer asynchronously on the same tick this widget mounts.
    private loadJournalRows(): void {
        this.loading = true;
        this.error = null;

        const userId = this.viewScopeService.getCurrentOpertaingUserId();

        this.viewScopeService.selectedCustomer$.pipe(
            filter((customer): customer is Customer => customer != null),
            take(1),
            switchMap(customer => {
                const apiUrl = this.ajaxService.getBaseOfApi() + DISP_JR_FOR_USER + customer.id + '/' + userId;
                return this.ajaxService.getData(apiUrl);
            }),
            catchError(error => {
                console.error('JournalMoveDistWidget - API error', error);
                this.error = this.translateServ.dictionary['FAILED_LOAD_DATA'];
                this.loading = false;
                return of({ Table: [] });
            })
        ).subscribe((response: any) => {
            this.loading = false;
            this.setSegments(response?.Table ?? []);
        });
    }

    private setSegments(rows: JournalTypeRow[]): void {
        const journalRows = rows.filter(row => row.MT === 0 || row.MT === 1);
        this.totalRows = journalRows.reduce((sum, row) => sum + Number(row.JournalRows || 0), 0);

        if (this.totalRows <= 0) {
            this.segments = [];
            this.setChart();
            return;
        }

        this.segments = journalRows.map(row => ({
            mt: row.MT,
            label: row.MT === 0 ? this.translateServ.dictionary['INVOICES_JOR'] : this.translateServ.dictionary['RECEIPTS_JOR'],
            rows: Number(row.JournalRows || 0),
            percent: Math.round((Number(row.JournalRows || 0) / this.totalRows) * 100)
        }));

        this.setChart();
    }

    private setChart(): void {
        const documentStyle = getComputedStyle(document.documentElement);

        // Primary main color for MT = 0
        const invoiceColor = documentStyle.getPropertyValue('--p-primary-color').trim()
            || this.layoutService.layoutConfig().primary
            || '#3b82f6';

        // 50% opacity version of main primary color for MT = 1
        const receiptColor = invoiceColor.startsWith('#')
            ? `${invoiceColor}80`
            : invoiceColor.replace('rgb', 'rgba').replace(')', ', 0.5)');

        this.chartData = {
            labels: this.segments.map(segment => `${segment.label} (${segment.percent}%)`),
            datasets: [
                {
                    data: this.segments.map(segment => segment.rows),
                    backgroundColor: this.segments.map(segment => segment.mt === 0 ? invoiceColor : receiptColor),
                    hoverBackgroundColor: this.segments.map(segment => segment.mt === 0 ? invoiceColor : receiptColor),
                    borderColor: documentStyle.getPropertyValue('--p-content-background').trim() || '#ffffff',
                    borderWidth: 3
                }
            ]
        };

        // Helper function to resolve dynamic text color directly from DOM at render time
        const getResolvedTextColor = () => {
            const style = getComputedStyle(document.documentElement);
            return style.getPropertyValue('--text-color').trim()
                || style.getPropertyValue('--p-text-color').trim()
                || (this.layoutService.isDarkTheme() ? '#ffffff' : '#111827');
        };

        const initialTextColor = getResolvedTextColor();

        this.chartOptions = {
            maintainAspectRatio: false,
            cutout: '68%',
            plugins: {
                legend: {
                    position: this.isRtl ? 'right' : 'left',
                    labels: {
                        color: initialTextColor,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: 18
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context: any) => {
                            const segment = this.segments[context.dataIndex];
                            return `${segment.label}: ${segment.rows} ${this.translateServ.dictionary['JOURNAL_MOVES_TITLE']} (${segment.percent}%)`;
                        }
                    }
                }
            },
            color: initialTextColor
        };

        // Canvas plugin dynamically fetching exact computed color on every canvas draw
        const totalRowsVal = this.totalRows;
        this.chartPlugins = [{
            id: 'centerTextPlugin',
            afterDraw: (chart: any) => {
                if (!chart.getDatasetMeta(0)?.data?.length) return;

                const { ctx } = chart;
                const meta = chart.getDatasetMeta(0);
                const x = meta.data[0].x;
                const y = meta.data[0].y;

                // Dynamically resolve computed theme text color live during draw loop
                const dynamicTextColor = getResolvedTextColor();

                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Subtitle
                ctx.font = '13px sans-serif';
                ctx.fillStyle = dynamicTextColor;
                ctx.fillText(this.translateServ.dictionary['TOTAL_JORS'], x, y - 10);

                // Total Count Number
                ctx.font = 'bold 24px sans-serif';
                ctx.fillStyle = dynamicTextColor;
                ctx.fillText(totalRowsVal.toString(), x, y + 15);

                ctx.restore();
            }
        }];
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}